import React from 'react';
import { FormDrawer } from './FormDrawer';
import { PO_ITEM_TYPE_COLORS, normalizePoItemType } from '../placeholders/purchaseOrderSummary';

const STATUS_OPTIONS = [
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Confirmada', label: 'Confirmada' },
  { value: 'Anulada', label: 'Anulada' },
];

export const PurchaseReceiptsForm = ({
  isOpen,
  onClose,
  onSave,
  onConfirm,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  formError = '',
  purchaseOrderOptions = [],
}) => {
  const items = Array.isArray(formData?.items) ? formData.items : [];
  const status = String(formData?.status || 'Borrador').trim() || 'Borrador';
  const canConfirm = status.toLowerCase() !== 'confirmada' && status.toLowerCase() !== 'anulada';

  const updateItem = (index, key, value) => {
    const next = items.map((item, idx) => (idx === index ? { ...item, [key]: value } : item));
    onChange('items', next);
  };

  const receiveAll = () => {
    onChange(
      'items',
      items.map((item) => ({
        ...item,
        quantity_received: item.quantity_ordered ?? item.quantity_received ?? 0,
      }))
    );
  };

  const sections = [
    {
      key: 'basic',
      title: 'Datos de la recepción',
      fields: [
        { key: 'receipt_code', label: 'Código', disabled: true, readOnly: true },
        {
          key: 'purchase_order_id',
          label: 'Orden de compra',
          type: 'searchable-select',
          placeholder: 'Buscar OC o proveedor…',
          options: purchaseOrderOptions,
        },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: STATUS_OPTIONS,
        },
        { key: 'receipt_date', label: 'Fecha recepción', type: 'date' },
        { key: 'received_by', label: 'Recibido por' },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 3 },
      ],
    },
    {
      key: 'items',
      title: 'Ítems recibidos',
      fields: [
        {
          key: 'items',
          fullWidth: true,
          type: 'custom',
          render: ({ colors, baseInputStyle }) => {
            const cellInput = {
              ...baseInputStyle,
              marginTop: 0,
              height: 32,
              fontSize: 12,
            };
            const thStyle = {
              padding: '8px 10px',
              textAlign: 'left',
              fontSize: 11,
              fontWeight: 700,
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
              borderBottom: `1px solid ${colors.sectionBorder}`,
            };
            const tdStyle = {
              padding: '8px 10px',
              verticalAlign: 'middle',
              borderBottom: `1px solid ${colors.sectionBorder}`,
            };
            return (
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: colors.muted }}>
                  Solo se recepcionan ítems de inventario (materias primas, insumos y upselling). Servicios y transporte no aplican.
                </span>
                {items.length ? (
                  <button
                    type="button"
                    onClick={receiveAll}
                    style={{
                      height: 28,
                      padding: '0 10px',
                      borderRadius: 6,
                      border: `1px solid ${colors.sectionBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Recibir todo
                  </button>
                ) : null}
              </div>
              {items.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>
                  Sin ítems. Elige una orden de compra primero.
                </p>
              ) : (
                <div
                  style={{
                    overflowX: 'auto',
                    border: `1px solid ${colors.sectionBorder}`,
                    borderRadius: 8,
                    background: colors.inputBg || 'transparent',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Tipo</th>
                        <th style={thStyle}>Código</th>
                        <th style={thStyle}>Descripción</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Pedido</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Recibido</th>
                        <th style={thStyle}>Unidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const typeLabel = normalizePoItemType(item.item_type || '');
                        const typeColor = PO_ITEM_TYPE_COLORS[typeLabel] || '#64748b';
                        return (
                          <tr key={item.purchase_order_item_id || item.id || `line-${index}`}>
                            <td style={tdStyle}>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  height: 24,
                                  alignItems: 'center',
                                  padding: '0 8px',
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: typeColor,
                                  background: `${typeColor}22`,
                                  border: `1px solid ${typeColor}55`,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.item_type || '—'}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, fontSize: 12, color: colors.text, whiteSpace: 'nowrap' }}>
                              {item.item_code || '—'}
                            </td>
                            <td style={{ ...tdStyle, fontSize: 12, color: colors.text }}>
                              {item.description || '—'}
                            </td>
                            <td style={{ ...tdStyle, fontSize: 12, color: colors.text, textAlign: 'right' }}>
                              {item.quantity_ordered ?? 0}
                            </td>
                            <td style={{ ...tdStyle, width: 110 }}>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                style={{ ...cellInput, textAlign: 'right' }}
                                value={item.quantity_received ?? 0}
                                onChange={(e) => updateItem(index, 'quantity_received', e.target.value)}
                              />
                            </td>
                            <td style={{ ...tdStyle, fontSize: 12, color: colors.muted }}>
                              {item.unit || 'UN'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            );
          },
        },
      ],
    },
  ];

  return (
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={isEditing ? 'Editar recepción' : 'Nueva recepción'}
      subtitle="Adquisiciones"
      sections={sections}
      formData={formData}
      onChange={onChange}
      formError={formError}
      width={920}
      footerActions={[
        { key: 'cancel', label: 'Cancelar', onClick: onClose },
        { key: 'draft', label: isSaving ? 'Guardando…' : 'Guardar borrador', onClick: () => onSave?.('Borrador') },
        ...(canConfirm && onConfirm
          ? [{ key: 'confirm', label: isSaving ? 'Confirmando…' : 'Confirmar recepción', variant: 'success', onClick: onConfirm }]
          : []),
      ]}
    />
  );
};

export default PurchaseReceiptsForm;
